import React from 'react';

import { NativeStackNavigationOptions, createNativeStackNavigator } from '@react-navigation/native-stack';

import { ds } from '@/components/lib/design.system';
import { screenPresets } from '@/navigation/screen-presets';

import { ModalStackDemoScreen } from './screens/ModalStackDemoScreen';
import { NativeHeaderCheckoutDemoScreen } from './screens/NativeHeaderCheckoutDemoScreen';
import { NativeHeaderDemoScreen } from './screens/NativeHeaderDemoScreen';
import { NativeHeaderScrollEdgeDemoScreen } from './screens/NativeHeaderScrollEdgeDemoScreen';
import { NavigationPresentationsDemo } from './screens/NavigationPresentationsDemo';
import { PresentationDemoScreen } from './screens/PresentationDemoScreen';
import { PresetFullScreenCoverDemoScreen } from './screens/PresetFullScreenCoverDemoScreen';
import { PresetPageDemoScreen } from './screens/PresetPageDemoScreen';
import { PresetSheetDemoScreen } from './screens/PresetSheetDemoScreen';
import { ScreenPresetsDemo } from './screens/ScreenPresetsDemo';

const DEMO_SHEET_DETENTS: NativeStackNavigationOptions['sheetAllowedDetents'] = [0.5, 1];

export type DevDemoStackParamList = {
    NavigationPresentationsDemo: undefined;
    ScreenPresetsDemo: undefined;
    NavDemoPresetPage: undefined;
    NavDemoPresetSheet: undefined;
    NavDemoPresetSheetDetents: undefined;
    NavDemoPresetFullScreenCover: undefined;
    NavDemoHeaderDefault: undefined;
    NavDemoHeaderDefaultTransparent: undefined;
    NavDemoHeaderLargeTitle: undefined;
    NavDemoHeaderWithButtons: undefined;
    NavDemoHeaderCheckout: undefined;
    NavDemoHeaderScrollEdge: undefined;
    NavDemoCard: undefined;
    NavDemoModal: undefined;
    NavDemoFormSheet: undefined;
    NavDemoFormSheetDetents: undefined;
    NavDemoFullScreen: undefined;
    NavDemoModalStack: undefined;
    NavDemoFullScreenStack: undefined;
    NavDemoTransparentModal: undefined;
    NavDemoContainedModal: undefined;
    NavDemoContainedTransparent: undefined;
    NavDemoAnimFade: undefined;
    NavDemoAnimNone: undefined;
    NavDemoAnimSimplePush: undefined;
    NavDemoAnimSlideFromBottom: undefined;
    NavDemoAnimFadeFromBottom: undefined;
    NavDemoAnimSlideFromRight: undefined;
    NavDemoCardImplicit: undefined;
};

const Stack = createNativeStackNavigator<DevDemoStackParamList>();

export const DevDemoStack = () => (
    <Stack.Navigator initialRouteName="NavigationPresentationsDemo" screenOptions={{ headerShown: false }}>
        <Stack.Screen name="NavigationPresentationsDemo" component={NavigationPresentationsDemo} />
        <Stack.Screen name="ScreenPresetsDemo" component={ScreenPresetsDemo} />
        <Stack.Screen name="NavDemoPresetPage" component={PresetPageDemoScreen} options={screenPresets.page} />
        <Stack.Screen name="NavDemoPresetSheet" component={PresetSheetDemoScreen} options={screenPresets.sheet()} />
        <Stack.Screen
            name="NavDemoPresetSheetDetents"
            component={PresetSheetDemoScreen}
            options={screenPresets.sheet(DEMO_SHEET_DETENTS)}
        />
        <Stack.Screen
            name="NavDemoPresetFullScreenCover"
            component={PresetFullScreenCoverDemoScreen}
            options={screenPresets.fullScreenCover}
        />
        <Stack.Screen
            name="NavDemoHeaderDefault"
            component={NativeHeaderDemoScreen}
            options={{
                headerShown: true,
                title: 'Default Header',
            }}
        />
        <Stack.Screen
            name="NavDemoHeaderDefaultTransparent"
            component={NativeHeaderDemoScreen}
            options={{
                headerShown: true,
                title: 'Default Transparent Header',
                headerTransparent: true,
                headerTintColor: ds.colorValue.text.primary,
            }}
        />
        <Stack.Screen
            name="NavDemoHeaderLargeTitle"
            component={NativeHeaderDemoScreen}
            options={{
                headerShown: true,
                headerLargeTitle: true,
                title: 'Large Title',
                headerTintColor: ds.colorValue.text.primary,
                headerLargeTitleStyle: { color: ds.colorValue.text.primary },
            }}
        />
        <Stack.Screen
            name="NavDemoHeaderWithButtons"
            component={NativeHeaderDemoScreen}
            options={{
                headerShown: true,
                headerLargeTitle: true,
                headerLargeTitleShadowVisible: false,
                title: 'With Buttons',
                headerTintColor: ds.colorValue.text.primary,
                headerLargeTitleStyle: { color: ds.colorValue.text.primary },
            }}
        />
        <Stack.Screen
            name="NavDemoHeaderCheckout"
            component={NativeHeaderCheckoutDemoScreen}
            options={{ presentation: 'modal', animation: 'slide_from_bottom', headerShown: false }}
        />
        <Stack.Screen
            name="NavDemoHeaderScrollEdge"
            component={NativeHeaderScrollEdgeDemoScreen}
            options={{
                headerShown: true,
                headerLargeTitle: true,
                headerLargeTitleShadowVisible: false,
                title: 'Scroll Edge Effects',
                headerTintColor: ds.colorValue.text.primary,
                headerLargeTitleStyle: { color: ds.colorValue.text.primary },
            }}
        />
        <Stack.Screen name="NavDemoCard" component={PresentationDemoScreen} options={{ presentation: 'card' }} />
        <Stack.Screen
            name="NavDemoModal"
            component={PresentationDemoScreen}
            options={{ presentation: 'modal', animation: 'slide_from_bottom' }}
        />
        <Stack.Screen
            name="NavDemoFormSheet"
            component={PresentationDemoScreen}
            options={{ presentation: 'formSheet' }}
        />
        <Stack.Screen
            name="NavDemoFormSheetDetents"
            component={PresentationDemoScreen}
            options={{
                presentation: 'formSheet',
                sheetAllowedDetents: [0.3, 0.8],
                sheetGrabberVisible: true,
                sheetInitialDetentIndex: 0,
                sheetLargestUndimmedDetentIndex: 1,
                sheetExpandsWhenScrolledToEdge: false,
                gestureEnabled: false,
            }}
        />
        <Stack.Screen
            name="NavDemoFullScreen"
            component={PresentationDemoScreen}
            options={{ presentation: 'fullScreenModal' }}
        />
        <Stack.Screen
            name="NavDemoModalStack"
            component={ModalStackDemoScreen}
            options={{ presentation: 'modal', headerShown: false }}
        />
        <Stack.Screen
            name="NavDemoFullScreenStack"
            component={ModalStackDemoScreen}
            options={{ presentation: 'fullScreenModal', headerShown: false }}
        />
        <Stack.Screen
            name="NavDemoTransparentModal"
            component={PresentationDemoScreen}
            options={{
                presentation: 'transparentModal',
                animation: 'slide_from_bottom',
                gestureEnabled: false,
            }}
        />
        <Stack.Screen
            name="NavDemoContainedModal"
            component={PresentationDemoScreen}
            options={{ presentation: 'containedModal', gestureEnabled: false }}
        />
        <Stack.Screen
            name="NavDemoContainedTransparent"
            component={PresentationDemoScreen}
            options={{ presentation: 'containedTransparentModal', gestureEnabled: false }}
        />
        <Stack.Screen name="NavDemoAnimFade" component={PresentationDemoScreen} options={{ animation: 'fade' }} />
        <Stack.Screen name="NavDemoAnimNone" component={PresentationDemoScreen} options={{ animation: 'none' }} />
        <Stack.Screen
            name="NavDemoAnimSimplePush"
            component={PresentationDemoScreen}
            options={{ animation: 'simple_push' }}
        />
        <Stack.Screen
            name="NavDemoAnimSlideFromBottom"
            component={PresentationDemoScreen}
            options={{ animation: 'slide_from_bottom' }}
        />
        <Stack.Screen
            name="NavDemoAnimFadeFromBottom"
            component={PresentationDemoScreen}
            options={{ animation: 'fade_from_bottom' }}
        />
        <Stack.Screen
            name="NavDemoAnimSlideFromRight"
            component={PresentationDemoScreen}
            options={{ animation: 'slide_from_right' }}
        />
        <Stack.Screen name="NavDemoCardImplicit" component={PresentationDemoScreen} />
    </Stack.Navigator>
);
